/**
 * Order History Component
 *
 * Displays user's purchase history with order details.
 * Fetches orders from backend and shows status, items, and totals.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import navigationService from '../services/navigationService';
import logger from '../utils/logger';
import './OrderHistory.css';

const OrderHistory = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      logger.log('⚠️ User not authenticated, redirecting to home');
      navigationService.navigateTo('home');
    }
  }, [authLoading, isAuthenticated]);

  // Fetch order history
  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated || !user?.uid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Call backend to get user's orders
        const response = await fetch('/.netlify/functions/get-orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.uid,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const data = await response.json();
        setOrders(data.orders || []);
        logger.log(`📦 Loaded ${data.orders?.length || 0} orders`);
      } catch (err) {
        console.error('❌ Error fetching orders:', err);
        setError('Unable to load order history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchOrders();
    }
  }, [isAuthenticated, user, authLoading]);

  // Toggle order details expansion
  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format price
  const formatPrice = (cents) => {
    if (typeof cents !== 'number') return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Get status badge info
  const getStatusBadge = (status) => {
    const statusMap = {
      completed: { color: '#27ae60', icon: 'check_circle', label: 'Completed' },
      processing: { color: '#3498db', icon: 'hourglass_empty', label: 'Processing' },
      shipped: { color: '#9b59b6', icon: 'local_shipping', label: 'Shipped' },
      canceled: { color: '#e74c3c', icon: 'cancel', label: 'Canceled' },
      refunded: { color: '#95a5a6', icon: 'undo', label: 'Refunded' },
    };

    return statusMap[status] || statusMap.completed;
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <>
        <MainHeader currentPage="orders" />
        <div className="order-history loading">
          <div className="loading-message">Loading your orders...</div>
        </div>
      </>
    );
  }

  // Not authenticated (shouldn't reach here due to redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <MainHeader currentPage="orders" />
      <div className="order-history">
        <div className="orders-container">
          {/* Page Header */}
          <div className="orders-header">
            <div className="orders-header-content">
              <h1>Order History</h1>
              <p className="orders-subtitle">View your past purchases and order status</p>
            </div>
            <button
              className="back-to-account-btn"
              onClick={() => navigationService.navigateTo('account')}
            >
              <MaterialIcon icon="arrow_back" size={20} />
              Back to Account
            </button>
          </div>

          {/* Error State */}
          {error && (
            <div className="orders-error">
              <MaterialIcon icon="error" size={32} />
              <p>{error}</p>
              <button className="retry-btn" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!error && orders.length === 0 && (
            <div className="orders-empty">
              <MaterialIcon icon="shopping_bag" size={80} />
              <h2>No Orders Yet</h2>
              <p>
                You haven't placed any orders yet. Start shopping to see your order history here!
              </p>
              <button className="shop-now-btn" onClick={() => navigationService.navigateTo('home')}>
                <MaterialIcon icon="storefront" size={20} />
                Browse Stickers
              </button>
            </div>
          )}

          {/* Orders List */}
          {!error && orders.length > 0 && (
            <div className="orders-list">
              {orders.map((order) => {
                const isExpanded = expandedOrders.has(order.id);
                const statusBadge = getStatusBadge(order.status);

                return (
                  <div key={order.id} className={`order-card ${isExpanded ? 'expanded' : ''}`}>
                    {/* Order Header (Always Visible) */}
                    <div className="order-header" onClick={() => toggleOrderExpansion(order.id)}>
                      <div className="order-header-left">
                        <div className="order-date">
                          <MaterialIcon icon="calendar_today" size={16} />
                          {formatDate(order.createdAt)}
                        </div>
                        <div className="order-id">Order #{order.id.slice(-8).toUpperCase()}</div>
                      </div>
                      <div className="order-header-right">
                        <div
                          className="order-status-badge"
                          style={{ backgroundColor: statusBadge.color }}
                        >
                          <MaterialIcon icon={statusBadge.icon} size={16} />
                          {statusBadge.label}
                        </div>
                        <div className="order-total">{formatPrice(order.total)}</div>
                        <MaterialIcon icon={isExpanded ? 'expand_less' : 'expand_more'} size={24} />
                      </div>
                    </div>

                    {/* Order Details (Expandable) */}
                    {isExpanded && (
                      <div className="order-details">
                        {/* Order Items */}
                        <div className="order-items-section">
                          <h3>Items ({order.items?.length || 0})</h3>
                          <div className="order-items">
                            {order.items?.map((item, index) => (
                              <div key={index} className="order-item">
                                <div className="order-item-image">
                                  {item.image ? (
                                    <img src={item.image} alt={item.name} />
                                  ) : (
                                    <MaterialIcon icon="image" size={32} />
                                  )}
                                </div>
                                <div className="order-item-info">
                                  <div className="order-item-name">{item.name}</div>
                                  <div className="order-item-details">
                                    {item.size && <span>Size: {item.size}</span>}
                                    {item.color && <span>Color: {item.color}</span>}
                                    <span>Qty: {item.quantity}</span>
                                  </div>
                                </div>
                                <div className="order-item-price">
                                  {formatPrice(item.price * item.quantity)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Order Summary */}
                        <div className="order-summary">
                          <div className="summary-row">
                            <span>Subtotal:</span>
                            <span>{formatPrice(order.subtotal)}</span>
                          </div>
                          {order.shipping > 0 && (
                            <div className="summary-row">
                              <span>Shipping:</span>
                              <span>{formatPrice(order.shipping)}</span>
                            </div>
                          )}
                          {order.tax > 0 && (
                            <div className="summary-row">
                              <span>Tax:</span>
                              <span>{formatPrice(order.tax)}</span>
                            </div>
                          )}
                          <div className="summary-row summary-total">
                            <span>Total:</span>
                            <span>{formatPrice(order.total)}</span>
                          </div>
                        </div>

                        {/* Shipping Address (if available) */}
                        {order.shippingAddress && (
                          <div className="order-shipping">
                            <h3>Shipping Address</h3>
                            <div className="shipping-address">
                              <div>{order.shippingAddress.name}</div>
                              <div>{order.shippingAddress.line1}</div>
                              {order.shippingAddress.line2 && (
                                <div>{order.shippingAddress.line2}</div>
                              )}
                              <div>
                                {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
                                {order.shippingAddress.postal_code}
                              </div>
                              <div>{order.shippingAddress.country}</div>
                            </div>
                          </div>
                        )}

                        {/* Tracking Info (if shipped) */}
                        {order.trackingNumber && (
                          <div className="order-tracking">
                            <MaterialIcon icon="local_shipping" size={20} />
                            <span>Tracking: {order.trackingNumber}</span>
                            {order.trackingUrl && (
                              <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="tracking-link"
                              >
                                Track Package
                                <MaterialIcon icon="open_in_new" size={16} />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderHistory;
